import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";

const STATE_META = {
    configured_healthy:              { color: "emerald", Icon: CheckCircle2, label: "Healthy" },
    configured_no_callbacks_yet:     { color: "amber",   Icon: Loader2,      label: "Configured, waiting for first callback" },
    configured_unmatched_detected:   { color: "amber",   Icon: AlertTriangle,label: "Unmatched callbacks detected" },
    signature_validation_failures:   { color: "rose",    Icon: XCircle,      label: "Signature validation failures detected" },
    callback_url_missing:            { color: "slate",   Icon: AlertTriangle,label: "Not configured" },
    credentials_missing:             { color: "rose",    Icon: XCircle,      label: "Twilio credentials missing" },
};

function StateBadge({ state }) {
    const m = STATE_META[state] || { color: "slate", Icon: AlertTriangle, label: state || "Unknown" };
    const { color, Icon, label } = m;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border bg-${color}-50 text-${color}-800 border-${color}-200`}
            data-testid="twilio-callback-state-badge"
            data-state={state || ""}
        >
            <Icon className={`h-3.5 w-3.5 text-${color}-700`} />
            {label}
        </span>
    );
}

/**
 * P1A.1 — Compact Twilio delivery-status callback section for Settings →
 * Providers. Loads live status from `/api/admin/twilio-status-callback/status`
 * and lets an admin edit the canonical callback URL that P1A's send paths
 * append as `StatusCallback` on every outbound Twilio Message.
 *
 * Save path: relies on the parent Settings page's `save()` which now
 * includes `twilio_status_callback_url` in its PUT payload. This
 * component's own "Save URL" button also calls PUT with ONLY the URL
 * field so an admin can update the URL without touching other secrets.
 */
export function TwilioCallbackSection({ s, setS }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/twilio-status-callback/status");
            setStatus(data);
        } catch (e) {
            // Non-admin users still see the settings page; suppress the
            // toast for 403 and just show a friendly placeholder.
            if ((e?.response?.status || 0) !== 403) {
                console.warn("callback status load failed", formatApiError(e));
            }
            setStatus(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const suggested = window?.location?.origin
        ? `${window.location.origin}/api/webhooks/twilio/message-status`
        : "";

    const saveJustUrl = async () => {
        const raw = (s.twilio_status_callback_url || "").trim();
        if (raw) {
            try {
                const u = new URL(raw);
                if (!s.test_mode && u.protocol !== "https:") {
                    toast_error("Callback URL must use HTTPS in live mode.");
                    return;
                }
            } catch {
                toast_error("Callback URL is malformed.");
                return;
            }
        }
        setSaving(true);
        try {
            // PUT with ONLY the URL field so we do NOT overwrite Account SID
            // (blank UI state) or Auth Token (masked value). The backend
            // ignores masked/blank secrets on the same PUT, but sending the
            // narrowest possible payload makes the intent obvious.
            await api.put("/settings", { twilio_status_callback_url: raw });
            toast_success("Callback URL saved");
            refresh();
        } catch (e) {
            toast_error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="rounded-lg border border-border bg-white px-4 py-4 space-y-3"
            data-testid="twilio-callback-section"
        >
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-[240px]">
                    <div className="text-sm font-medium text-gray-900">Twilio Delivery Status Callback</div>
                    <div className="text-xs text-muted-foreground">
                        Twilio POSTs message lifecycle updates (sent / delivered / undelivered / failed) to this URL.
                        Leave blank to disable — CaseFlow will only record the initial accept response.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <StateBadge state={status?.state} />
                    <Button size="sm" variant="ghost" onClick={refresh}
                        disabled={loading}
                        data-testid="callback-refresh-status">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        <span className="ml-1">Refresh</span>
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                    <Label className="text-xs">Callback URL</Label>
                    <Input
                        value={s.twilio_status_callback_url || ""}
                        onChange={e => setS({ ...s, twilio_status_callback_url: e.target.value })}
                        placeholder={suggested || "https://…/api/webhooks/twilio/message-status"}
                        data-testid="twilio-callback-url-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Must be HTTPS in live mode. Suggested value for this environment:
                        <code className="ml-1 font-mono text-[10px]">{suggested || "—"}</code>
                    </p>
                </div>
                <Button size="sm" onClick={saveJustUrl} disabled={saving} data-testid="callback-save-url">
                    {saving ? "Saving…" : "Save URL"}
                </Button>
            </div>

            {status && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" data-testid="callback-counters">
                    <Metric label="Last callback"
                        value={status.last_callback_at ? new Date(status.last_callback_at).toLocaleString() : "—"}
                        testid="callback-last-received" />
                    <Metric label="24h received" value={status.callbacks_24h ?? 0} testid="callback-24h-total" />
                    <Metric label="24h unmatched" value={status.unmatched_24h ?? 0}
                        tone={status.unmatched_24h ? "amber" : "default"}
                        testid="callback-24h-unmatched" />
                    <Metric label="24h invalid sig" value={status.invalid_signature_24h ?? 0}
                        tone={status.invalid_signature_24h ? "rose" : "default"}
                        testid="callback-24h-invalid-sig" />
                </div>
            )}
        </div>
    );
}

function Metric({ label, value, tone = "default", testid }) {
    const cls =
        tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" :
        tone === "rose"  ? "border-rose-200 bg-rose-50 text-rose-900" :
                           "border-border bg-muted/40 text-gray-800";
    return (
        <div className={`rounded-md border px-2 py-1.5 ${cls}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
            <div className="font-semibold truncate">{value}</div>
        </div>
    );
}

// Small helpers — sonner is imported lazily to keep the component's
// surface area minimal (unit tests can import the component without
// pulling in the toast side-effects).
function toast_success(msg) {
    import("sonner").then(({ toast }) => toast.success(msg));
}
function toast_error(msg) {
    import("sonner").then(({ toast }) => toast.error(msg));
}

export default TwilioCallbackSection;
